import React, { useState } from 'react';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image: string;
  category: string;
  stock: number;
  isActive: boolean;
  createdAt: string;
}

const MisProductos: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([
    {
      id: '1',
      name: 'Martillo Demoledor Profesional',
      description: 'Martillo demoledor de 20kg con sistema anti-vibración para trabajos pesados de demolición',
      price: 45000,
      image: 'https://placehold.co/300x200?text=Martillo+Demoledor',
      category: 'Herramientas',
      stock: 12,
      isActive: true,
      createdAt: '2024-01-15'
    },
    {
      id: '2',
      name: 'Cemento Portland Premium',
      description: 'Cemento de alta resistencia para estructuras y cimientos, 50kg por bolsa',
      price: 8500,
      image: 'https://placehold.co/300x200?text=Cemento+Portland',
      category: 'Materiales',
      stock: 150,
      isActive: true,
      createdAt: '2024-01-10'
    },
    {
      id: '3',
      name: 'Andamio Modular 2x1.5m',
      description: 'Sistema de andamio modular con plataforma de trabajo y barandillas de seguridad',
      price: 35000,
      image: 'https://placehold.co/300x200?text=Andamio+Modular',
      category: 'Equipos',
      stock: 8,
      isActive: true,
      createdAt: '2024-01-05'
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    description: '',
    price: 0,
    image: '',
    category: '',
    stock: 0,
    isActive: true
  });

  const categories = ['Herramientas', 'Materiales', 'Equipos', 'Maquinaria', 'Seguridad', 'Otros'];

  const handleAddProduct = () => {
    if (newProduct.name && newProduct.description && newProduct.price) {
      const product: Product = {
        id: Date.now().toString(),
        name: newProduct.name,
        description: newProduct.description,
        price: newProduct.price,
        image: newProduct.image || 'https://placehold.co/300x200?text=Producto',
        category: newProduct.category || 'Otros',
        stock: newProduct.stock || 0,
        isActive: newProduct.isActive || true,
        createdAt: new Date().toISOString().split('T')[0]
      };
      
      setProducts([...products, product]);
      setNewProduct({
        name: '',
        description: '',
        price: 0,
        image: '',
        category: '',
        stock: 0,
        isActive: true
      });
      setShowAddModal(false);
    }
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setNewProduct(product);
    setShowAddModal(true);
  };

  const handleUpdateProduct = () => {
    if (editingProduct && newProduct.name && newProduct.description && newProduct.price) {
      const updatedProducts = products.map(p => 
        p.id === editingProduct.id 
          ? { ...p, ...newProduct }
          : p
      );
      setProducts(updatedProducts);
      setEditingProduct(null);
      setNewProduct({
        name: '',
        description: '',
        price: 0,
        image: '',
        category: '',
        stock: 0,
        isActive: true
      });
      setShowAddModal(false);
    }
  };

  const handleDeleteProduct = (productId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este producto?')) {
      setProducts(products.filter(p => p.id !== productId));
    }
  };

  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(p => 
      p.id === productId 
        ? { ...p, isActive: !p.isActive }
        : p
    ));
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(price);
  };

  return (
    <div className="mis-productos-section">
      <div className="section-header">
        <h2>Mis Productos</h2>
        <p>Administra tu catálogo de productos y servicios</p>
      </div>

      {/* Stats Cards */}
      <div className="grid-4" style={{ marginBottom: '2rem' }}>
        <div className="dashboard-card">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-box"></i>
            </div>
            <div className="stat-content">
              <h3>{products.length}</h3>
              <p>Total Productos</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-check-circle"></i>
            </div>
            <div className="stat-content">
              <h3>{products.filter(p => p.isActive).length}</h3>
              <p>Productos Activos</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-dollar-sign"></i>
            </div>
            <div className="stat-content">
              <h3>{formatPrice(products.reduce((sum, p) => sum + p.price, 0))}</h3>
              <p>Valor Total</p>
            </div>
          </div>
        </div>
        <div className="dashboard-card">
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-warehouse"></i>
            </div>
            <div className="stat-content">
              <h3>{products.reduce((sum, p) => sum + p.stock, 0)}</h3>
              <p>Stock Total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Products List */}
      <div className="dashboard-card">
        <div className="card-header">
          <h3 className="card-title">Catálogo de Productos</h3>
          <div className="card-actions">
            <button 
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <i className="fas fa-plus"></i>
              Agregar Producto
            </button>
          </div>
        </div>

        <div className="products-grid">
          {products.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-image">
                <img src={product.image} alt={product.name} />
                <div className="product-status">
                  <span className={`status-badge ${product.isActive ? 'status-active' : 'status-inactive'}`}>
                    {product.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>
              <div className="product-info">
                <h4>{product.name}</h4>
                <p className="product-description">{product.description}</p>
                <div className="product-details">
                  <span className="product-category">{product.category}</span>
                  <span className="product-price">{formatPrice(product.price)}</span>
                </div>
                <div className="product-stock">
                  <span>Stock: {product.stock} unidades</span>
                </div>
                <div className="product-actions">
                  <button 
                    className="btn btn-secondary"
                    onClick={() => handleEditProduct(product)}
                  >
                    <i className="fas fa-edit"></i>
                    Editar
                  </button>
                  <button 
                    className={`btn ${product.isActive ? 'btn-danger' : 'btn-success'}`}
                    onClick={() => toggleProductStatus(product.id)}
                  >
                    <i className={`fas fa-${product.isActive ? 'pause' : 'play'}`}></i>
                    {product.isActive ? 'Desactivar' : 'Activar'}
                  </button>
                  <button 
                    className="btn btn-danger"
                    onClick={() => handleDeleteProduct(product.id)}
                  >
                    <i className="fas fa-trash"></i>
                    Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add/Edit Product Modal */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingProduct ? 'Editar Producto' : 'Agregar Nuevo Producto'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingProduct(null);
                  setNewProduct({
                    name: '',
                    description: '',
                    price: 0,
                    image: '',
                    category: '',
                    stock: 0,
                    isActive: true
                  });
                }}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Nombre del Producto</label>
                  <input
                    type="text"
                    value={newProduct.name || ''}
                    onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                    className="form-input"
                    placeholder="Ej: Laptop Gaming Pro"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Categoría</label>
                  <select
                    value={newProduct.category || ''}
                    onChange={(e) => setNewProduct({...newProduct, category: e.target.value})}
                    className="form-input"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Descripción</label>
                <textarea
                  value={newProduct.description || ''}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  className="form-input form-textarea"
                  placeholder="Describe tu producto..."
                  rows={4}
                />
              </div>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label">Precio (ARS)</label>
                  <input
                    type="number"
                    value={newProduct.price || ''}
                    onChange={(e) => setNewProduct({...newProduct, price: Number(e.target.value)})}
                    className="form-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Stock</label>
                  <input
                    type="number"
                    value={newProduct.stock || ''}
                    onChange={(e) => setNewProduct({...newProduct, stock: Number(e.target.value)})}
                    className="form-input"
                    placeholder="0"
                    min="0"
                  />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">URL de la Imagen</label>
                <input
                  type="url"
                  value={newProduct.image || ''}
                  onChange={(e) => setNewProduct({...newProduct, image: e.target.value})}
                  className="form-input"
                  placeholder="https://ejemplo.com/imagen.jpg"
                />
              </div>
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={newProduct.isActive || false}
                    onChange={(e) => setNewProduct({...newProduct, isActive: e.target.checked})}
                  />
                  Producto Activo
                </label>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddModal(false);
                  setEditingProduct(null);
                  setNewProduct({
                    name: '',
                    description: '',
                    price: 0,
                    image: '',
                    category: '',
                    stock: 0,
                    isActive: true
                  });
                }}
              >
                Cancelar
              </button>
              <button 
                className="btn btn-primary"
                onClick={editingProduct ? handleUpdateProduct : handleAddProduct}
              >
                {editingProduct ? 'Actualizar Producto' : 'Agregar Producto'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MisProductos;
